import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import EmptyState from '@atlaskit/empty-state';
import { RequiredAsterisk } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/glyph/add';
import styled from '@emotion/styled';
import Button from '@atlaskit/button';
import LinkExternalIcon from '@atlaskit/icon/utility/link-external';
import { N500 } from '@atlaskit/theme/colors';
import { AnalyticsTrackAction, AnalyticsTrackSubject, sendAnalyticsEvent } from '@atlassian/temp-analytics';
import {
  FetchPaymentMethodsOfTransactionAccountForDetailPageQuery,
  FetchAssociatedBillingAccountsQuery,
  FetchAssociatedBillingAccountsQueryVariables,
  PaymentMethodTypeV2,
  PaymentOnTerms,
  TermPaymentTypes,
  PayPal,
} from '@css-xp/graphtypes/src/generated-types-new';
import { getCreditUsageUiUrlViaIdentity } from '@css-xp/ppc-navigation';
import { emptyStatePropForPaymentMethods } from '@css-xp/ppc-common/src/state-props';
import { LayoutAppearance } from '@css-xp/ppc-types';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { OnUpdateCallbacks, OpenSidebarButton, useApplicationContext, useSidebar } from '@css-xp/base';
import {
  failureMessage,
  getSupportUrl,
  TableHeading,
  getCurrencyByCountry,
  Currency,
  fetchAssociatedBillingAccounts,
  LimitCurrencyDropdown,
} from '@css-xp/ppc-common';
import { PaymentMethodsLoader } from '@css-xp/ppc-page-loaders';
import { shouldShowACHPaymentMethod } from '@css-xp/ppc-utils';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { CreditCardPayment } from '../credit-card-payment';
import { translateErrorMessage } from './stripe-error-translator';
import { DeferredPayment } from '../deferred-payment';
import { PayPalPayment } from '../paypal-payment';
import { AchPayment } from '../ach-payment';
import { CARD_PAYMENTS_SIDEBAR } from '../../../common/types';
import './payment-methods.scss';

export const StyledHigherPaymentTerms = styled.div`
  margin-left: 10px;

  > button {
    color: ${N500} !important;
  }
`;

export type PartnerPaymentMethodsProps = {
  label?: string;
  data: FetchPaymentMethodsOfTransactionAccountForDetailPageQuery | undefined;
  loading: boolean;
  refetch: () => void;
  sidebarClassName?: string;
  isDesignIteration1Enabled?: boolean;
  layoutAppearance?: LayoutAppearance;
  isPaypalSupportEnabled?: boolean;
  isMultiCurrencyChangesEnabled?: boolean;
  isACHPaymentMethodEnabled?: boolean;
  selectedPartnerCountry?: string;
  creditCardHeading?: string;
};

export const PaymentMethods: React.FC<PartnerPaymentMethodsProps> = ({
  data,
  loading,
  refetch,
  sidebarClassName,
  isDesignIteration1Enabled = false,
  layoutAppearance = LayoutAppearance.DEFAULT,
  isPaypalSupportEnabled,
  isMultiCurrencyChangesEnabled,
  isACHPaymentMethodEnabled,
  selectedPartnerCountry,
  creditCardHeading,
}) => {
  const { txaId } = useParams<{ txaId: string }>();
  const { closeSidebar } = useSidebar();
  const { email } = useApplicationContext();

  const initialCurrency = getCurrencyByCountry(data?.transactionAccount?.billToParty?.country) || Currency.USD;
  const [currency, setCurrency] = useState<string>(initialCurrency.toUpperCase());

  const associatedBillingAccountsResponse = useQuery<
    FetchAssociatedBillingAccountsQuery,
    FetchAssociatedBillingAccountsQueryVariables
  >(fetchAssociatedBillingAccounts, {
    variables: { id: txaId, currency },
    fetchPolicy: 'no-cache',
    skip: !isMultiCurrencyChangesEnabled && !txaId,
  });

  const associatedBillingAccounts = associatedBillingAccountsResponse?.data?.transactionAccount?.billingAccountsList;

  const billingAccountsList = useMemo(() => {
    return isMultiCurrencyChangesEnabled ? associatedBillingAccounts : data?.transactionAccount?.billingAccountsList;
  }, [isMultiCurrencyChangesEnabled, data, associatedBillingAccounts]);

  const allPaymentMethods = data?.transactionAccount.availablePaymentMethods?.nodes;
  const cardPaymentMethods = useMemo(
    () => allPaymentMethods?.filter((paymentMethod: any) => paymentMethod?.type === PaymentMethodTypeV2.CARD) ?? [],
    [allPaymentMethods]
  );
  const deferredPaymentMethods = useMemo(
    () =>
      (allPaymentMethods?.filter(
        (paymentMethod: any) => paymentMethod?.type === PaymentMethodTypeV2.DEFERRED
      ) as PaymentOnTerms[]) ?? [],
    [allPaymentMethods]
  );

  const paypalPaymentMethods: any = useMemo(
    () =>
      (allPaymentMethods?.filter(
        (paymentMethod: any) => paymentMethod?.type === PaymentMethodTypeV2.PAYPAL
      ) as PayPal[]) ?? [],
    [allPaymentMethods]
  );

  const achPaymentMethods: any = useMemo(
    () =>
      isACHPaymentMethodEnabled
        ? allPaymentMethods?.filter((paymentMethod: any) => paymentMethod?.type === PaymentMethodTypeV2.ACH)
        : [],
    [allPaymentMethods, isACHPaymentMethodEnabled]
  );

  const hasNoCardPaymentMethods: boolean = cardPaymentMethods?.length === 0;
  const hasNoDeferredPaymentMethods: boolean = deferredPaymentMethods?.length === 0;
  const hasNoPaypalPaymentMethods: boolean = paypalPaymentMethods?.length === 0;
  const hasNoPaymentMethodsAvailable = hasNoCardPaymentMethods && hasNoDeferredPaymentMethods;
  const hasPayOnAccountPaymentMethod = deferredPaymentMethods?.[0]?.terms?.payOnStatement ?? false;
  const hasNoACHPaymentMethods: boolean = achPaymentMethods?.length === 0;

  const sendAddCreditCardEvent = () => {
    sendAnalyticsEvent(AnalyticsTrackAction.ADDED, AnalyticsTrackSubject.ADD_CREDIT_CARD);
  };

  const sendAddCreditCardSuccessEvent = () => {
    sendAnalyticsEvent(AnalyticsTrackAction.SUCCESSFUL, AnalyticsTrackSubject.ADD_CREDIT_CARD);
  };

  const sendAddCreditCardFailureEvent = () => {
    sendAnalyticsEvent(AnalyticsTrackAction.FAILED, AnalyticsTrackSubject.ADD_CREDIT_CARD);
  };

  const onUpdateCallbacks: OnUpdateCallbacks = {
    onUpdateSuccess: () => {
      sendAddCreditCardSuccessEvent();
      closeSidebar();
    },
    onUpdateFailure: () => {
      sendAddCreditCardFailureEvent();
      closeSidebar();
    },
    onUpdateDone: () => {
      sendAddCreditCardEvent();
      refetch();
    },
    successMessage: 'Payment method added',
    failureMessage: failureMessage(),
    translateErrorMessage,
  };
  const isOneColumnLayout = layoutAppearance === LayoutAppearance.ONE_COLUMN;

  const renderAddPaymentButton = () => {
    let subHeader = (
      <p>We accept all major debit/credit cards. For help with other ways to pay, contact {getSupportUrl()}.</p>
    );

    if (isPaypalSupportEnabled) {
      subHeader = (
        <p>
          We accept all major debit/credit cards and PayPal. For help with other ways to pay, contact {getSupportUrl()}.
        </p>
      );
    }

    let oneColumnSubHeader = (
      <>
        <p>We accept all major debit/credit cards.</p>
        <br />
        <p>
          Required fields are marked with an asterisk <RequiredAsterisk />
        </p>
      </>
    );

    if (isPaypalSupportEnabled) {
      oneColumnSubHeader = (
        <>
          <p>We accept all major debit/credit cards and PayPal. Contact {getSupportUrl()} for other ways to pay.</p>
          <br />
          <p>
            Required fields are marked with an asterisk <RequiredAsterisk />
          </p>
        </>
      );
    }

    if (isACHPaymentMethodEnabled) {
      oneColumnSubHeader = (
        <>
          <p>We accept all major debit/credit cards and ACH. Contact {getSupportUrl()} for other ways to pay.</p>
        </>
      );
    }

    if (isACHPaymentMethodEnabled && isPaypalSupportEnabled) {
      oneColumnSubHeader = (
        <>
          <p>
            We accept all major debit/credit cards, PayPal and ACH. Contact {getSupportUrl()} for other ways to pay.
          </p>
        </>
      );
    }

    return (
      <AdvocateAccessWall accessType={ADVOCATE_ACCESS_TYPE.CREATE} resource={ADVOCATE_RESOURCE_TYPE.PAYMENT_METHOD}>
        <OpenSidebarButton
          appearance="default"
          testId="ppc-add-payment-method"
          sidebar={CARD_PAYMENTS_SIDEBAR.ADD_PAYMENT_METHOD}
          payload={{
            txaId,
            operation: 'Add',
            onCancel: closeSidebar,
            sidebarClassName,
            showSubscriptionChargeNote: true,
            subHeader: isOneColumnLayout ? oneColumnSubHeader : subHeader,
            ...onUpdateCallbacks,
            version: isOneColumnLayout ? 2 : undefined,
            header: isOneColumnLayout ? 'Add payment method' : undefined,
            isPaypalSupportEnabled,
            payPalEmails: paypalPaymentMethods.map((payPal: any) => payPal?.payPal?.email),
            shouldShowACHPaymentMethod: shouldShowACHPaymentMethod(selectedPartnerCountry, isACHPaymentMethodEnabled),
          }}
          sidebarOptions={{
            isOverlay: true,
            sidebarWidth: !isOneColumnLayout ? '600px' : undefined,
            sidebarTitle: 'Add payment method',
          }}
          iconBefore={<AddIcon label="add" size="small" />}
        >
          Add payment method
        </OpenSidebarButton>
        {isDesignIteration1Enabled && !hasPayOnAccountPaymentMethod && (
          <StyledHigherPaymentTerms>
            <Button
              onClick={() => {
                window.open(getCreditUsageUiUrlViaIdentity({ transactionAccountId: txaId, email }));
              }}
              iconAfter={<LinkExternalIcon label="" />}
            >
              Apply for higher payment terms
            </Button>
          </StyledHigherPaymentTerms>
        )}
      </AdvocateAccessWall>
    );
  };

  const renderEmptyState = () => {
    return isDesignIteration1Enabled ? (
      <EmptyState {...emptyStatePropForPaymentMethods(renderAddPaymentButton(), false)} />
    ) : (
      <EmptyState {...emptyStatePropForPaymentMethods(renderAddPaymentButton())} />
    );
  };

  const renderPaymentTermsSection = () => {
    if (hasNoDeferredPaymentMethods) {
      return null;
    }

    return (
      <>
        <h2 className="ppc-payment-method-section-heading">Payment terms</h2>
        {isMultiCurrencyChangesEnabled && (
          <div className="currency-dropdown">
            <LimitCurrencyDropdown
              currency={currency}
              setCurrency={setCurrency}
              label="View credits in"
              showLabel
              isDesignIteration1Enabled={isDesignIteration1Enabled}
              isLightTheme={true}
            />
          </div>
        )}
        {deferredPaymentMethods.map((deferredPayment: PaymentOnTerms) => {
          let paymentMethodFromInvoiceDueDays = null;
          if (deferredPayment?.terms?.invoiceDueDays === 14) {
            paymentMethodFromInvoiceDueDays = TermPaymentTypes.NET14;
          } else if (deferredPayment?.terms?.invoiceDueDays === 30) {
            paymentMethodFromInvoiceDueDays = TermPaymentTypes.NET30;
          }
          return (
            // The component DeferredPayment does not take the `key` prop, hence eslint-disable is added for react/jsx-key.
            // eslint-disable-next-line react/jsx-key
            <DeferredPayment
              txaId={txaId}
              creditLimit={deferredPayment?.totalCreditLimit}
              paymentMethod={paymentMethodFromInvoiceDueDays ?? deferredPayment?.paymentMethod}
              billingAccountsList={billingAccountsList}
              hasPayByStatement={deferredPayment?.terms?.payOnStatement ?? false}
              invoiceDueDays={deferredPayment?.terms?.invoiceDueDays}
              version={isOneColumnLayout ? 2 : undefined}
              isLoading={associatedBillingAccountsResponse?.loading}
              isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
            />
          );
        })}
      </>
    );
  };

  const renderCardPaymentMethodsSection = () => {
    if (hasNoCardPaymentMethods) {
      return null;
    }

    return (
      <>
        <h2 className="ppc-payment-method-section-heading">{creditCardHeading || 'Credit cards'}</h2>
        {cardPaymentMethods.map((card: any, index: any) => {
          return (
            <CreditCardPayment
              key={`${card.last4Numbers}-${index}`}
              txaId={txaId}
              card={card}
              onUpdateDone={refetch}
              isOneColumnLayout={isOneColumnLayout}
            />
          );
        })}
      </>
    );
  };

  const renderOtherPaymentsSection = () => {
    if (hasNoPaypalPaymentMethods && hasNoACHPaymentMethods) {
      return null;
    }

    return (
      <>
        <h2 className="ppc-payment-method-section-heading">Other</h2>
        {paypalPaymentMethods.map((payPal: any, index: any) => {
          return (
            <PayPalPayment
              key={`${index}`}
              txaId={txaId}
              isDesignIteration1Enabled={isDesignIteration1Enabled}
              payPal={payPal}
              onUpdateDone={refetch}
            />
          );
        })}
        {isACHPaymentMethodEnabled &&
          achPaymentMethods.map((ach: any, index: any) => {
            return <AchPayment key={`${index}`} ach={ach} onUpdateDone={refetch} txaId={txaId} />;
          })}
      </>
    );
  };

  return (
    <div
      data-testid="ppc-payment-methods-wrapper"
      className={`ppc-payment-methods-wrapper ${isOneColumnLayout ? 'ppc-payment-methods-wrapper-v2' : ''}`}
    >
      <div className={`ppc-payment-methods-container ${loading ? 'loading' : ''}`}>
        {loading ? (
          isDesignIteration1Enabled ? (
            <PaymentMethodsLoader />
          ) : (
            <LoadingIndicator />
          )
        ) : (
          <>
            <div className="ppc-payment-methods">
              {!isOneColumnLayout && <TableHeading>Payment methods</TableHeading>}
              {hasNoPaymentMethodsAvailable ? (
                renderEmptyState()
              ) : (
                <div className="ppc-payment-method-actions">{renderAddPaymentButton()}</div>
              )}
              {renderPaymentTermsSection()}
              {renderCardPaymentMethodsSection()}
              {(isPaypalSupportEnabled || isACHPaymentMethodEnabled) && renderOtherPaymentsSection()}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
